import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiCornerDownRight, FiEyeOff, FiRadio, FiSend, FiTrash2 } from 'react-icons/fi';
import { cAddComment, cComments, cDeleteComment } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import AudioPlayer from './AudioPlayer';
import Linkify from './Linkify';
import UserAvatar from './UserAvatar';
import UserBadges from './UserBadges';
import VoiceRecorder from './VoiceRecorder';
import { BLUE, timeAgo } from './brand';

/**
 * Comments of one article: list + reply + delete (author or post owner).
 * A comment can be text, a **voice note** (mic button) or both.
 *
 *   · replying to somebody **auto mentions** them: the server puts
 *     `@username` in front of the reply and stores `reply_username`, so the
 *     answer always sits under the main comment with the handle visible;
 *   · when the author turned comments off (“បិទការទទួលមតិ”) the form is
 *     replaced by a short explanation and nothing can be posted.
 */
export default function CommentList({ postId, postOwnerId, onCount, canComment = true }) {
  const { t, lang } = useLanguage();
  const { user, requireAuth, actingChannel } = useCommunity();
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    cComments(postId)
      .then((rows) => setItems(Array.isArray(rows) ? rows : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [postId]);

  const send = async (e) => {
    e.preventDefault();
    if (!requireAuth(t('loginRequiredDesc'))) return;
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      await cAddComment(postId, {
        text: body,
        parent_id: replyTo ? replyTo.id : null,
        channel_id: actingChannel ? actingChannel.id : null,
      });
      setText('');
      setReplyTo(null);
      load();
      if (onCount) onCount(1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  /** A recorded voice note was uploaded — post it as a voice comment. */
  const sendVoice = async ({ url }) => {
    try {
      await cAddComment(postId, {
        text: '',
        audio: url,
        parent_id: replyTo ? replyTo.id : null,
        channel_id: actingChannel ? actingChannel.id : null,
      });
      setReplyTo(null);
      load();
      if (onCount) onCount(1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const remove = async (comment) => {
    if (!window.confirm(t('deleteCommentConfirm'))) return;
    try {
      await cDeleteComment(comment.id);
      setItems((prev) => prev.filter((c) => c.id !== comment.id));
      if (onCount) onCount(-1);
      toast.success(t('deletedOk'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const roots = items.filter((c) => !c.parent_id);
  const repliesOf = (id) => items.filter((c) => c.parent_id === id);

  const Row = ({ c, reply = false }) => (
    <div className={`flex gap-2.5 ${reply ? 'ml-9 mt-2' : 'mt-3'}`}>
      <UserAvatar
        user={c.author}
        size={reply ? 30 : 36}
        to={c.author
          ? (c.author.channel ? `/channel/${c.author.channel_slug}` : `/u/${c.author.username}`)
          : null}
      />
      <div className="min-w-0 flex-1">
        <div className="rounded-2xl px-3.5 py-2 bg-gray-100 dark:bg-gray-800">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{c.author?.name || c.author?.username}</span>
            <UserBadges user={c.author} />
            {/* written “as a channel” */}
            {c.author?.channel && (
              <Link
                to={`/channel/${c.author.channel_slug}`}
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"
                style={{ background: `${BLUE}18`, color: BLUE }}
              >
                <FiRadio className="w-3 h-3" /> {t('channelPostBadge')}
              </Link>
            )}
            <span className="text-[11px] text-gray-400">{timeAgo(c.created_at, lang)}</span>
            {/* the automatic mention of the member this reply answers */}
            {reply && c.reply_username && (
              <span className="text-[11px] font-semibold inline-flex items-center gap-1" style={{ color: BLUE }}>
                <FiCornerDownRight className="w-3 h-3" />
                {t('replyTo')} @{c.reply_username}
              </span>
            )}
          </div>
          {reply && c.reply_name && (
            <span className="block text-[11px] text-gray-400">
              {c.reply_name}
            </span>
          )}
          {c.audio && (
            <span className="block mt-1.5">
              <AudioPlayer src={c.audio} compact />
            </span>
          )}
          <Linkify text={c.text} className="text-sm text-gray-800 dark:text-gray-100 mt-0.5" />
        </div>
        <div className="flex items-center gap-3 px-2 pt-1 text-[11px] text-gray-500">
          {!reply && (
            <button
              type="button"
              onClick={() => { setReplyTo(c); if (!requireAuth(t('loginRequiredDesc'))) return; }}
              className="hover:text-gray-800 dark:hover:text-gray-200 font-semibold inline-flex items-center gap-1"
            >
              <FiCornerDownRight className="w-3 h-3" /> {t('reply')}
            </button>
          )}
          {user && (user.id === c.user_id || user.id === postOwnerId) && (
            <button
              type="button"
              onClick={() => remove(c)}
              className="hover:text-red-600 font-semibold inline-flex items-center gap-1"
            >
              <FiTrash2 className="w-3 h-3" /> {t('delete')}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {!canComment && (
        <p className="mt-1 mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5">
          <FiEyeOff className="w-3.5 h-3.5" /> {t('commentsOff')}
        </p>
      )}
      {canComment && (
      <form onSubmit={send} className="mt-1">
        {actingChannel && (
          <p className="mb-1.5 inline-flex items-center gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1.5 text-xs font-bold text-amber-800 dark:text-amber-200">
            <FiRadio className="w-3.5 h-3.5" />
            {t('commentAsChannel').replace('{name}', actingChannel.name)}
          </p>
        )}
        {replyTo && (
          <div className="mb-1.5 flex items-center gap-2 text-xs text-gray-500">
            <FiCornerDownRight className="w-3.5 h-3.5" />
            {t('reply')} · {replyTo.author?.name || replyTo.author?.username}
            <button type="button" className="underline" onClick={() => setReplyTo(null)}>{t('cancel')}</button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <UserAvatar user={user} size={34} />
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => { if (!requireAuth(t('loginRequiredDesc'))) return; }}
            rows={1}
            placeholder={t('commentPlaceholder')}
            className="flex-1 resize-none rounded-2xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3.5 py-2 text-sm focus:border-gray-400 outline-none"
          />
          <VoiceRecorder kind="voice" onUploaded={sendVoice} />
          <button
            type="submit"
            data-sfx="send"
            disabled={busy || !text.trim()}
            className="p-2.5 rounded-full text-white disabled:opacity-50"
            style={{ background: BLUE }}
            aria-label={t('sendMessage')}
          >
            <FiSend className="w-4 h-4" />
          </button>
        </div>
      </form>
      )}

      {loading ? (
        <p className="text-xs text-gray-400 mt-3">{t('loading')}</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-gray-400 mt-3">{t('writeAComment')}</p>
      ) : (
        roots.map((c) => (
          <div key={c.id}>
            <Row c={c} />
            {repliesOf(c.id).map((r) => <Row key={r.id} c={r} reply />)}
          </div>
        ))
      )}
    </div>
  );
}
